import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Phone, Sparkles, Check, ArrowRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingCTA from "@/components/site/FloatingCTA";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CONTACT } from "@/data/content";

const heroImg =
  "https://images.unsplash.com/photo-1571086291540-b137111fa1c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMGFpcnBsYW5lJTIwbG9hZGluZ3xlbnwwfHx8fDE3ODM4OTYwMDN8MA&ixlib=rb-4.1.0&q=85";

const LANES = [
  {
    code: "GB",
    flag: "🇬🇧",
    name: "United Kingdom",
    lane: "DEL → LHR / STN / MAN",
    detail:
      "4-day DDP to any UK postcode. UK CDS clearance, VAT + duty settled on our end via LHR partner desk. Recipient receives cleanly — no CBSA-style holds.",
  },
  {
    code: "US",
    flag: "🇺🇸",
    name: "United States",
    lane: "DEL → JFK / LAX / ORD",
    detail:
      "4–5 day DDP to any US ZIP. CBP entry, duties + MPF + HMF settled on our credit; FDA MoCRA / FCC / DOT compliance validated at pre-alert.",
  },
  {
    code: "KR",
    flag: "🇰🇷",
    name: "South Korea",
    lane: "DEL → ICN",
    detail:
      "4-day DDP to any Korean address. KCS entry, K-BIS + K-FDA compliance handled by our ICN Incheon partner desk. Fastest lane on this list.",
  },
  {
    code: "AE",
    flag: "🇦🇪",
    name: "UAE (Dubai)",
    lane: "DEL → DXB / AUH / SHJ",
    detail:
      "3–4 day DDP to Dubai, Abu Dhabi, Sharjah. Dubai Customs entry, VAT + duty settled on our end; ESMA/Dubai Municipality compliance handled where required.",
  },
  {
    code: "VN",
    flag: "🇻🇳",
    name: "Vietnam",
    lane: "DEL → SGN / HAN",
    detail:
      "4-day DDP to any Vietnamese address. Vietnam Customs entry, import VAT + duty settled on our credit; ASEAN-India Trade in Goods Agreement (AIFTA) concessional duty applied where eligible.",
  },
  {
    code: "TH",
    flag: "🇹🇭",
    name: "Thailand",
    lane: "DEL → BKK / DMK",
    detail:
      "4-day DDP to any Thai address. Thai Customs entry, import VAT + duty settled on our end; AIFTA concessional duty applied where eligible; FDA-Thailand handling for food/cosmetics.",
  },
];

const FAQS = [
  {
    q: "Can I export from India under LUT (zero-rated GST) via Custom Clearance Partner?",
    a: "Yes. Indian exports are zero-rated under GST law — manufacturers and exporters ship under a Letter of Undertaking (LUT) executed with the GST department, which means no GST is charged on the export invoice and no GST refund cycle is triggered. Custom Clearance Partner files the export Shipping Bill against your LUT, handles ICEGATE and DGFT reconciliation, and delivers duty-paid to the destination consignee's door. Perfect for manufacturers and exporters who want to avoid GST cash blockage.",
  },
  {
    q: "How fast is 4-day DDP export from Delhi to UK / USA / Korea / UAE / Vietnam / Thailand?",
    a: "From Delhi (DEL) as origin: UAE 3–4 days, UK / Korea / Vietnam / Thailand 4 days, USA 4–5 days — all door-to-door DDP end-to-end. Timing is measured from cargo hand-off at our Greater Noida hub to signed POD at destination consignee's door.",
  },
  {
    q: "What is DDP export and why do manufacturers prefer it?",
    a: "DDP (Delivered Duty Paid) is an Incoterm where the exporter delivers goods to the destination address with all import duty and taxes pre-paid on the buyer's behalf. Manufacturers prefer DDP because: (1) the overseas buyer receives cleanly with no delivery-day duty surprise, (2) it dramatically improves conversion for cross-border sales, and (3) it lets the exporter offer a single all-inclusive landed price — a huge sales advantage.",
  },
  {
    q: "Do you handle export documentation, Shipping Bills, and DGFT filings?",
    a: "Yes. Custom Clearance Partner is a licensed Indian Customs Broker (CBLR 2018). We file the export Shipping Bill at ICEGATE, handle DGFT reconciliation for MEIS/RoDTEP claims where applicable, generate the eBRC-ready commercial invoice, and manage all EDI-linked reporting to Indian Customs.",
  },
  {
    q: "Who is this export DDP service designed for?",
    a: "Indian manufacturers, D2C brands, wholesalers, and big exporters shipping to overseas B2B buyers, B2C end-customers, or their own overseas warehouses. Common industries: garments and textiles, handicrafts, engineered goods, jewellery, ayurveda / wellness, food, home décor, spices, and marketplace consolidations for Amazon US/UK/UAE FBA.",
  },
  {
    q: "How much does 4-day DDP export from Delhi cost?",
    a: "Per-kg all-inclusive DDP rates are quoted per destination and per commodity. For most standard commercial cargo from Delhi: UAE ~₹950/kg, Korea ~₹1,050/kg, UK ~₹1,200/kg, Vietnam / Thailand ~₹1,100/kg, USA ~₹1,350/kg. Volume slabs discount 15–30% for regular monthly consignments. WhatsApp +91 78003 20078 with your product HSN + destination for a firm quote.",
  },
  {
    q: "Do you handle Amazon FBA / marketplace fulfilment exports from India?",
    a: "Yes. We consolidate manufacturer output from Delhi (and elsewhere in India), file the export Shipping Bill under your LUT, ship air-express DDP to Amazon FBA warehouses in the US, UK, UAE, and other markets — including Amazon carton-label placement, ISF filing for US, and PAN-EU VAT-compliant delivery for UK.",
  },
  {
    q: "Can I export samples and small consignments in express mode?",
    a: "Yes. Small samples and B2B consignments up to 30 kg are cleared under India's Courier Shipping Bill regime for maximum speed. Larger commercial consignments file under standard Shipping Bill. We pick the correct regime shipment-by-shipment to optimise both duty and turnaround.",
  },
];

export default function ExportFromIndia() {
  useEffect(() => {
    document.title =
      "Export from India DDP to UK, USA, Korea, UAE, Vietnam, Thailand | 4-Day Delhi Air Express | LUT Zero-Rated GST | Custom Clearance Partner";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "4-day DDP export from Delhi to UK, USA, South Korea, UAE (Dubai), Vietnam, and Thailand. Licensed Indian Customs Broker files Shipping Bill under LUT (zero-rated GST, no cash blockage). Designed for manufacturers, big exporters, D2C brands, and Amazon FBA sellers. WhatsApp +91 78003 20078.";

    let kw = document.querySelector('meta[name="keywords"][data-export]');
    if (!kw) {
      kw = document.createElement("meta");
      kw.name = "keywords";
      kw.setAttribute("data-export", "1");
      document.head.appendChild(kw);
    }
    kw.content =
      "export from India DDP, India to UK DDP export, India to USA DDP export, India to Korea DDP export, India to UAE Dubai export, India to Vietnam export, India to Thailand export, LUT export from India, zero-rated GST export India, 4-day express export Delhi, manufacturer export from India, Amazon FBA export from India, D2C export from India, big exporter Delhi DDP, no GST export India LUT";

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = "https://customclearancepartner.in/export-from-india-ddp";

    const id = "ccp-export-jsonld";
    document.getElementById(id)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://customclearancepartner.in/export-from-india-ddp",
          name: "4-Day DDP Export from India (Delhi) to UK, USA, Korea, UAE, Vietnam, Thailand",
          inLanguage: "en-IN",
          about: { "@id": "https://customclearancepartner.in/#org" },
          speakable: {
            "@type": "SpeakableSpecification",
            xpath: ["//*[@id='export-h1']", "//*[@id='export-tldr']"],
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://customclearancepartner.in/" },
            { "@type": "ListItem", position: 2, name: "Export from India DDP", item: "https://customclearancepartner.in/export-from-india-ddp" },
          ],
        },
        {
          "@type": "Service",
          name: "4-Day DDP Export from India — Delhi to UK, USA, Korea, UAE, Vietnam, Thailand",
          alternateName: [
            "Export from India DDP",
            "LUT export from India",
            "Zero-rated GST export India",
            "4-day express export Delhi",
            "Manufacturer export from India",
            "Amazon FBA export from India",
            "D2C export from India",
            "India DDP to UK USA Korea UAE Vietnam Thailand",
          ],
          serviceType: "Export Customs and DDP Air Freight",
          areaServed: [
            { "@type": "Country", name: "United Kingdom" },
            { "@type": "Country", name: "United States" },
            { "@type": "Country", name: "South Korea" },
            { "@type": "Country", name: "United Arab Emirates" },
            { "@type": "Country", name: "Vietnam" },
            { "@type": "Country", name: "Thailand" },
          ],
          provider: { "@id": "https://customclearancepartner.in/#org" },
          description:
            "Licensed Indian Customs Broker files export Shipping Bill under LUT (zero-rated GST, no cash blockage), handles DGFT reconciliation for MEIS/RoDTEP, and delivers duty-paid door-to-door in 4 days from Delhi to UK, USA, South Korea, UAE, Vietnam, and Thailand.",
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(script);
    return () => document.getElementById(id)?.remove();
  }, []);

  return (
    <div className="grain min-h-screen bg-[#F9FAFB] text-[#101828]" data-testid="export-page">
      <Header />
      <section className="border-b border-[#E4E7EC] bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <nav className="font-mono-accent flex items-center gap-3 text-[#475467]" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D92D20]">HOME</Link>
            <span>/</span>
            <span className="text-[#101828]">EXPORT FROM INDIA · DDP</span>
          </nav>

          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="overline mb-4 flex items-center gap-3">
                <Sparkles className="h-4 w-4 text-[#D92D20]" strokeWidth={2.5} />
                For Indian manufacturers &amp; big exporters
              </p>
              <h1
                id="export-h1"
                data-testid="export-h1"
                className="font-display text-4xl font-black leading-[0.98] tracking-tight text-[#101828] sm:text-5xl md:text-6xl lg:text-7xl"
              >
                4-day DDP export from <span className="text-[#D92D20]">Delhi.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#344054] md:text-xl">
                To <strong>UK, USA, South Korea, UAE (Dubai), Vietnam, and
                Thailand</strong>. Filed under <strong>LUT (zero-rated GST)</strong>{" "}
                — no GST outflow, no refund cycle, no cash blockage. Overseas
                buyer receives duty-paid at the door.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={CONTACT.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="export-whatsapp-hero"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#101828] bg-[#25D366] px-6 py-4 font-semibold text-[#0B111A] shadow-hard hover:bg-[#1FBE5A]"
                >
                  <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
                  Get an export quote
                </a>
                <a
                  href={CONTACT.telHref}
                  data-testid="export-call-hero"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#101828] bg-white px-6 py-4 font-semibold text-[#101828] hover:bg-[#101828] hover:text-white"
                >
                  <Phone className="h-5 w-5" strokeWidth={2.5} />
                  {CONTACT.phoneDisplay}
                </a>
              </div>

              <div
                data-testid="export-lut-callout"
                className="mt-6 flex items-start gap-3 border-2 border-[#101828] bg-[#25D366]/10 p-4"
              >
                <Check className="mt-0.5 h-5 w-5 flex-none text-[#25D366]" strokeWidth={3} />
                <p className="text-sm leading-relaxed text-[#101828]">
                  <strong>LUT export — zero-rated GST, no cash blockage.</strong>{" "}
                  Indian exports are zero-rated under GST law. Manufacturers
                  ship under a Letter of Undertaking (LUT), so no GST is charged
                  on the export invoice and no refund cycle is triggered. We
                  file your Shipping Bill against your LUT and handle DGFT
                  reconciliation for MEIS / RoDTEP where applicable.
                </p>
              </div>

              <div
                id="export-tldr"
                data-testid="export-tldr"
                className="mt-6 border-2 border-[#101828] bg-[#FDB022] p-5 md:p-6"
              >
                <p className="font-mono-accent text-[#101828]">TL;DR — DIRECT ANSWER</p>
                <p className="mt-2 text-base leading-relaxed text-[#101828]">
                  Indian manufacturer, D2C brand, or big exporter shipping to
                  UK, USA, Korea, UAE, Vietnam, or Thailand?{" "}
                  <strong>4-day door-to-door DDP from Delhi</strong>, filed
                  under <strong>LUT (zero-rated GST)</strong>, licensed Indian
                  Customs Broker handles the Shipping Bill + DGFT + destination
                  clearance. Perfect for Amazon FBA, cross-border D2C, or
                  wholesale B2B lanes. WhatsApp{" "}
                  <a href={CONTACT.whatsappHref} className="font-bold underline">
                    +91 78003 20078
                  </a>{" "}
                  with HSN + destination for a firm quote.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden border-2 border-[#101828]">
                <img
                  src={heroImg}
                  alt="India air cargo export"
                  className="h-72 w-full object-cover md:h-96"
                  loading="eager"
                />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="border-2 border-[#101828] bg-white p-3">
                  <div className="font-display text-xl font-black leading-none text-[#101828]">4 days</div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#475467]">DEL DOOR-TO-DOOR</div>
                </div>
                <div className="border-2 border-[#101828] bg-[#101828] p-3">
                  <div className="font-display text-xl font-black leading-none text-[#FDB022]">LUT</div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#98A2B3]">ZERO-RATED GST</div>
                </div>
                <div className="border-2 border-[#101828] bg-[#FDB022] p-3">
                  <div className="font-display text-xl font-black leading-none text-[#101828]">DDP</div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#101828]">DUTY PAID</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-[#E4E7EC] bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <p className="overline mb-4">§ Six destination lanes</p>
          <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-[#101828] sm:text-4xl md:text-5xl">
            Delhi origin. Six destination markets. DDP door delivery.
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {LANES.map((d, i) => (
              <motion.article
                key={d.code}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                className="border-2 border-[#101828] bg-[#F9FAFB] p-6"
                data-testid={`export-lane-${d.code}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl leading-none">{d.flag}</span>
                  <div>
                    <div className="font-display text-lg font-bold text-[#101828]">{d.name}</div>
                    <div className="font-mono-accent text-[#D92D20]">{d.code}</div>
                  </div>
                </div>
                <div className="font-mono-accent mt-4 text-[10px] text-[#475467]">{d.lane}</div>
                <p className="mt-3 text-sm leading-relaxed text-[#475467]">{d.detail}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-[#E4E7EC] bg-white">
        <div className="mx-auto max-w-4xl px-5 py-20 md:px-8 md:py-28">
          <p className="overline mb-4">§ Export FAQ</p>
          <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-[#101828] sm:text-4xl md:text-5xl">
            Straight answers for exporters.
          </h2>
          <div className="mt-10 border-2 border-[#101828] bg-[#F9FAFB]">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`ex-fq-${i}`}
                  className="border-b border-[#E4E7EC] last:border-b-0"
                  data-testid={`export-faq-${i}`}
                >
                  <AccordionTrigger className="px-5 py-4 text-left font-display text-base font-bold tracking-tight text-[#101828] hover:no-underline md:text-lg">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 text-sm leading-relaxed text-[#475467]">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="border-b border-[#E4E7EC] bg-[#D92D20] text-white">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center md:px-8">
          <p className="font-mono-accent text-white/80">READY TO EXPORT?</p>
          <h2 className="mt-4 font-display text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
            Send HSN, destination, monthly volume. 15-minute quote.
          </h2>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="export-whatsapp-bottom"
              className="inline-flex items-center justify-center gap-2 border-2 border-white bg-[#25D366] px-6 py-4 font-semibold text-[#0B111A] hover:bg-[#1FBE5A]"
            >
              <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
              WhatsApp +91 78003 20078
            </a>
            <a
              href={CONTACT.telHref}
              data-testid="export-call-bottom"
              className="inline-flex items-center justify-center gap-2 border-2 border-white bg-transparent px-6 py-4 font-semibold text-white hover:bg-white hover:text-[#D92D20]"
            >
              <Phone className="h-5 w-5" strokeWidth={2.5} />
              Call the desk
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}
